/**
 * EV Charging API
 * Endpoints for charging station management, sessions, and dashboard metrics
 */

import { apiClient } from "./client";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

import type {
  ChargingStation,
  ChargingSession,
  ChargingMeterValue,
  ChargingDashboard,
  HourlyPowerEntry,
  YearlySummaryEntry,
  ChargingDataRangeResponse,
  DailyEnergyEntry,
} from "./types";

/**
 * Get all registered charging stations
 */
export async function getChargingStations(): Promise<ChargingStation[]> {
  return apiClient.get<ChargingStation[]>("/api/v1/charging/stations");
}

/**
 * Get charging sessions with optional filters
 * @param stationId Filter by station ID
 * @param dateFrom Start date (YYYY-MM-DD)
 * @param dateTo End date (YYYY-MM-DD)
 */
export async function getChargingSessions(
  stationId?: number,
  dateFrom?: string,
  dateTo?: string,
): Promise<ChargingSession[]> {
  const params = new URLSearchParams();
  if (stationId) params.set("station_id", String(stationId));
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  const qs = params.toString();
  return apiClient.get<ChargingSession[]>(
    `/api/v1/charging/sessions${qs ? `?${qs}` : ""}`,
  );
}

/**
 * Get dashboard metrics (all 7 metric cards)
 */
export async function getChargingDashboard(): Promise<ChargingDashboard> {
  return apiClient.get<ChargingDashboard>("/api/v1/charging/dashboard");
}

/**
 * Get hourly power data for the 24-hour bar chart
 * @param targetDate Date (YYYY-MM-DD), defaults to today
 * @param stationId Optional station filter
 */
export async function getHourlyPower(
  targetDate?: string,
  stationId?: number,
): Promise<HourlyPowerEntry[]> {
  const params = new URLSearchParams();
  if (targetDate) params.set("target_date", targetDate);
  if (stationId) params.set("station_id", String(stationId));
  const qs = params.toString();
  return apiClient.get<HourlyPowerEntry[]>(
    `/api/v1/charging/hourly-power${qs ? `?${qs}` : ""}`,
  );
}

/**
 * Get yearly summary (stations, MWh, cost per year)
 */
export async function getYearlySummary(): Promise<YearlySummaryEntry[]> {
  return apiClient.get<YearlySummaryEntry[]>(
    "/api/v1/charging/yearly-summary",
  );
}

/**
 * Get available date range with meter data
 */
export async function getChargingDataRange(): Promise<ChargingDataRangeResponse> {
  return apiClient.get<ChargingDataRangeResponse>(
    "/api/v1/charging/data-range",
  );
}

/**
 * Get daily energy data for multi-day bar chart
 * @param startDate Start date (YYYY-MM-DD)
 * @param endDate End date (YYYY-MM-DD)
 * @param stationId Optional station filter
 */
export async function getDailyEnergy(
  startDate: string,
  endDate: string,
  stationId?: number,
): Promise<DailyEnergyEntry[]> {
  const params = new URLSearchParams();
  params.set("start_date", startDate);
  params.set("end_date", endDate);
  if (stationId) params.set("station_id", String(stationId));
  return apiClient.get<DailyEnergyEntry[]>(
    `/api/v1/charging/daily-energy?${params.toString()}`,
  );
}

// --- Station Registration ---

export interface StationRegistrationData {
  station_name: string;
  location?: string;
  vendor?: string;
  model?: string;
  serial_number?: string;
  capacity_kw?: number;
  connector_type?: string;
  comment?: string;
}

export interface StationRegistrationResponse {
  id: number;
  status: string;
  message: string;
  confirmation_sent: boolean;
  websocket_url: string | null;
  websocket_url_tls: string | null;
}

/**
 * Submit a new station registration request
 */
export async function submitStationRegistration(
  data: StationRegistrationData,
  atCommandsFile?: File,
  historicalDataFile?: File,
): Promise<StationRegistrationResponse> {
  const token = apiClient.getToken();
  if (!token) throw new Error("Authentication required");

  const formData = new FormData();
  formData.append("registration_data", JSON.stringify(data));
  if (atCommandsFile) formData.append("at_commands_file", atCommandsFile);
  if (historicalDataFile)
    formData.append("historical_data_file", historicalDataFile);

  const response = await fetch(
    `${API_BASE_URL}/api/v1/charging/station-registration/`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    },
  );

  if (!response.ok) {
    let errorDetail = "Failed to submit registration";
    try {
      const errorData = await response.json();
      errorDetail = errorData.detail || errorData.message || errorDetail;
    } catch {
      errorDetail = response.statusText || errorDetail;
    }
    if (response.status === 401) {
      apiClient.clearToken();
      if (typeof window !== "undefined") window.location.href = "/auth";
    }
    throw new Error(errorDetail);
  }

  return response.json();
}
